/**
 * FormTitle — the ONE shared on-form title shown above a grid/form
 * (CLAUDE.md §18.6).  It guarantees two app-wide rules at once:
 *
 *  1. Title text / icon / colour come from the SAME source the sidebar uses
 *     (navData → navMeta), so a form's heading can never drift from its
 *     navigation entry.  The glyph is rendered by the SAME shared <NavIcon>
 *     AppShell uses for the sidebar (emoji via TI_EMOJI, else the Tabler line
 *     glyph coloured by iconColors.css / the inline iconColor), so the icon
 *     before the form name matches the navigation icon + colour 1:1.
 *  2. The load-time indicator lives HERE — at the TOP, next to the title —
 *     and ONLY here.  The shared grid no longer shows a bottom load-time;
 *     every form surfaces it through this component (seconds normally,
 *     milliseconds when < 1 s, via the shared LoadTimeChip).
 *
 * Usage (single-id form):
 *     <FormTitle pageId="sampreg" loadMs={loadMs} loading={isFetching} />
 * Multi-id renderer (pass the active id):
 *     <FormTitle pageId={moduleKey} loadMs={loadMs} loading={isFetching} />
 * Descriptive sub-caption (keep nav icon/colour, override only the text):
 *     <FormTitle pageId="morreport" title="Saved Adhoc Reports" loadMs={loadMs} />
 * Hidden form not in nav (supply icon/title explicitly):
 *     <FormTitle icon="ti-file-analytics" title="Custom Report Builder" />
 */
import { navMeta } from '../layouts/navData';
import { LoadTimeChip } from './LoadTimeChip';
import { AuditTrailButton } from './AuditTrailButton';
import { NavIcon } from './NavIcon';

interface Props {
  /** Nav id — sources the icon, colour and (default) title text from navData. */
  pageId?: string;
  /** Icon override (Tabler `ti-*`).  Defaults to the nav icon for `pageId`. */
  icon?: string;
  /** Icon colour override.  Defaults to the nav item's inline colour (else the
   *  iconColors.css palette colour for the glyph). */
  iconColor?: string;
  /** Title text override.  Defaults to the nav label for `pageId`. */
  title?: string;
  /** Optional descriptive sub-caption shown as a muted second line under the
   *  title (e.g. MDL's "Method Detection Limit — 40 CFR 136 App. B, Rev 2").
   *  Backward-compatible: renders nothing when omitted, so existing single-line
   *  titles are unchanged. */
  subtitle?: string;
  /** Last fetch duration (ms) — shown as the shared TOP load-time chip. */
  loadMs?: number | null;
  /** True while fetching — the chip shows "Loading…". */
  loading?: boolean;
  /** Audit Trail — VIEW-ONLY, so it lives HERE on the title row (BO UI
   *  Standard v2 ruling 2026-07-01), never in the action toolbar.  When set,
   *  the shared config-gated <AuditTrailButton> renders as a QUIET violet
   *  chip right after the load-time chip (the read-only zone), on List and
   *  Detail alike.  `auditFormName` matches dbo.AuditData.FormName;
   *  `auditSourceId` optionally narrows to one record. */
  auditFormName?: string;
  auditSourceId?: string;
}

export function FormTitle({ pageId, icon, iconColor, title, subtitle, loadMs = null, loading, auditFormName, auditSourceId }: Props) {
  const meta = pageId ? navMeta(pageId) : undefined;
  const glyph = icon ?? meta?.icon ?? 'ti-table';
  const colour = iconColor ?? meta?.iconColor;
  const text = title ?? meta?.label ?? '';
  // BO UI Standard §7: the glyph sits in a small rounded square tinted with its
  // own colour (that colour at ~12% alpha) — a bare check in a light-green box
  // for Method, etc.  Only when we have a plain hex colour to tint from.
  const tint = colour && /^#[0-9a-fA-F]{6}$/.test(colour) ? `${colour}1f` : undefined;
  return (
    <>
      <div className="section-title">
        <span className="ft-ic" style={colour ? { color: colour, background: tint } : undefined}>
          <NavIcon icon={glyph} color={colour} />
        </span> {text}
        <LoadTimeChip ms={loadMs} loading={loading} />
        {auditFormName && (
          <AuditTrailButton variant="chip" formName={auditFormName} sourceId={auditSourceId} />
        )}
      </div>
      {subtitle && <div className="ft-sub">{subtitle}</div>}
    </>
  );
}
